import React, { useState } from "react";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  QuerySamplesForWorkbookQuery,
  useQuerySamplesForWorkbookQuery,
} from "features/Workbooks/WorkbookList/ChooseParameters/gql/Query.generated";
import { useCreateWorkbookParameterSetsMutation } from "features/Workbooks/WorkbookList/ChooseParameters/gql/Mutation.create.generated";
import { useDeleteWorkbookParameterSetsMutation } from "features/Workbooks/WorkbookList/ChooseParameters/gql/Mutation.delete.generated";
import { useDeleteWorkbookResultsMutation } from "features/Workbooks/WorkbookList/ChooseParameters/gql/Mutation.deleteResults.generated";
import { useDeleteWorkbookMutation } from "features/Workbooks/WorkbookList/WorkbookVariantSidebar/gql/Mutation.workbook.generated";
import { WorkbookDetailsDocument } from "features/Workbooks/WorkbookList/Workbook/gql/Query.generated";
import { WorkbooksDocument } from "features/Workbooks/WorkbookList/gql/Query.generated";
export type QuerySample =
  QuerySamplesForWorkbookQuery["getQuerySamplesForWorkbook"][number];
import { WorkbookType } from "features/Workbooks/WorkbookList/Workbook";

import { formatTimestampShort } from "utils/format";
import moment from "moment";
import Grid, { MsCell } from "ui/Grid";
import SQL from "ui/SQL";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faEdit,
  faClonePlus,
} from "@fortawesome/pro-regular-svg-icons";
import PanelSection from "ui/PanelSection";
import { useRoutes } from "utils/routes";
import Loading from "ui/Loading";
import {
  aliasParamMapToString,
  jsonParametersToString,
} from "features/Workbooks/WorkbookList/Workbook/util";
import CreateParameterSetPanel from "features/Workbooks/WorkbookList/CreateParameterSetPanel";
import EditParameterSetPanel from "features/Workbooks/WorkbookList/EditParameterSetPanel";
import Button from "ui/Button";
import { QueryTextArea } from "features/Workbooks/WorkbookList/ReviewQuery";
import EditParameterSettingsPanel from "features/Workbooks/WorkbookList/EditParameterSettingsPanel";
import {
  faCircle1,
  faCircle2,
  faCircle3,
  faCog,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import PillButtonBar from "ui/PillButtonBar";
import Callout from "ui/Callout";
import WorkbookNameEditPanel from "features/Workbooks/WorkbookList/WorkbookNameEditPanel";
import classNames from "classnames";
import { hasDuplicatedSamples } from "features/Workbooks/WorkbookList/ChooseParameters/util";
import ExplainFingerprint from "features/Explain/ExplainFingerprint";

type AddParamMethodType = "sample" | "query" | "manual";
type SelectedParameter = {
  fingerprint: string | null;
  runtimeMs: number | null;
  paramInSql: string;
  onEdit: () => void | null;
  onClone: (() => void) | null;
  onDelete: () => void;
  autoCreatedFromQuery: boolean;
};

const ChooseParameters = ({ workbook }: { workbook: WorkbookType }) => {
  const { databaseId } = useParams();

  const { loading, error, data } = useQuerySamplesForWorkbookQuery({
    variables: {
      databaseId,
      workbookId: workbook.id,
    },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const querySamples = data.getQuerySamplesForWorkbook;
  return (
    <ChooseParametersContent workbook={workbook} querySamples={querySamples} />
  );
};

const ChooseParametersContent = ({
  workbook,
  querySamples,
}: {
  workbook: WorkbookType;
  querySamples: QuerySample[];
}) => {
  const { databaseId } = useParams();
  const [sampleIds, setSampleIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageForSamples, setErrorMessageForSamples] = useState("");
  const [addParamMethod, setAddParamMethod] = useState<AddParamMethodType>(
    querySamples.length ? "sample" : "query",
  );
  // The parameter set to prefill the manual form's "Copy values from" with ("" = blank).
  const [copyFromId, setCopyFromId] = useState("");
  const [parameterSetIdForEdit, setParameterSetIdForEdit] = useState(null);
  const [showParameterSettingsEditPanel, setShowParameterSettingsEditPanel] =
    useState(false);
  const { databaseQueryExplain, databaseWorkbookVariantRun } = useRoutes();
  const navigate = useNavigate();
  const baselineQuery = workbook.baselineQuery;

  const [createWorkbookParameterSets] =
    useCreateWorkbookParameterSetsMutation();
  const [deleteWorkbookParameterSets] =
    useDeleteWorkbookParameterSetsMutation();

  const filteredData = querySamples.filter((sample) =>
    jsonParametersToString(sample.jsonParameters)
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase()),
  );

  function handleEditDismiss() {
    setParameterSetIdForEdit(null);
  }

  const handleRunExplain = () => {
    createWorkbookParameterSets({
      variables: {
        databaseId,
        variantId: baselineQuery.id,
        querySampleIds: sampleIds,
        finalize: true,
      },
      refetchQueries: [
        {
          query: WorkbookDetailsDocument,
          variables: { workbookId: workbook.id, databaseId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        navigate(
          databaseWorkbookVariantRun(
            workbook.databaseId,
            workbook.id,
            baselineQuery.id,
          ),
        );
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  const queryWithNoParams =
    Object.keys(baselineQuery.paramRefAliasMap).length === 0;
  const queryWithSamples = querySamples.length > 0;

  const workbookTitle = (
    <WorkbookCreationHeaderWithWorkbook workbook={workbook} />
  );
  const editParameterSettings = (
    <Button
      bare
      onClick={() => setShowParameterSettingsEditPanel(true)}
      variant="link"
    >
      <FontAwesomeIcon icon={faCog} className="mr-0.5" /> Edit parameter
      settings
    </Button>
  );
  const selectedParameters: SelectedParameter[] = [];
  sampleIds.forEach((sId) => {
    const sample = querySamples.find((s) => s.id === sId);
    selectedParameters.push({
      fingerprint: sample.explain?.fingerprint,
      runtimeMs: sample.runtimeMs,
      paramInSql: jsonParametersToString(sample.jsonParameters),
      onEdit: null,
      onClone: null,
      onDelete: () => {
        setSampleIds(sampleIds.filter((id) => id !== sId));
      },
      autoCreatedFromQuery: false,
    });
  });
  workbook.aliasParamMapList.forEach((paramMap) => {
    selectedParameters.push({
      fingerprint: null,
      runtimeMs: null,
      paramInSql: aliasParamMapToString(paramMap.parameters),
      onEdit: () => setParameterSetIdForEdit(paramMap.id),
      onClone: () => {
        setAddParamMethod("manual");
        setCopyFromId(paramMap.id);
      },
      onDelete: () => {
        deleteWorkbookParameterSets({
          variables: {
            workbookId: workbook.id,
            parameterSetId: paramMap.id,
          },
          onCompleted: () => {
            setErrorMessage("");
          },
          onError: (error) => {
            setErrorMessage(error.message);
          },
        });
      },
      autoCreatedFromQuery: paramMap.source === "auto_from_query",
    });
  });

  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      title={workbookTitle}
      pageCategory="workbooks"
      pageName="choose-parameters"
    >
      <Panel
        title={<CreateSteps step="step2" workbook={workbook} />}
        secondaryTitle={!queryWithNoParams && editParameterSettings}
      >
        <PanelSection className="grid gap-4">
          <div className="relative">
            <QueryTextArea
              queryText={workbook.baselineQuery.queryTextWithAlias}
              className="max-h-[120px] overflow-y-scroll"
              showCopyToClipboard
            />
          </div>
          <PillButtonBar
            opts={[
              {
                value: "sample",
                label: "Extract parameters from query samples",
              },
              { value: "query", label: "Extract parameters from query" },
              { value: "manual", label: "Specify parameters manually" },
            ]}
            selected={addParamMethod}
            onChange={(value) => {
              setErrorMessage("");
              setErrorMessageForSamples("");
              setCopyFromId("");
              setAddParamMethod(value);
            }}
            mode="stretch"
          />
          {addParamMethod === "sample" ? (
            <>
              <Callout thin>
                {queryWithSamples ? (
                  <>
                    We found the {querySamples.length} most interesting
                    parameter sets for you in your query samples. For the best
                    experience, select at least two distinct parameter sets.
                  </>
                ) : (
                  <>We did not find any parameter sets from query samples.</>
                )}
              </Callout>
              {queryWithSamples && (
                <>
                  <div className="w-full">
                    <input
                      className="bg-white rounded border border-gray-300 box-border h-8 leading-5 px-2 py-2 w-[400px]"
                      type="text"
                      placeholder="Search parameter sets..."
                      onChange={(e) => setSearchTerm(e.target.value)}
                      value={searchTerm}
                    />
                  </div>
                  <Grid
                    data={filteredData}
                    defaultSortBy="runtimeMs"
                    noRowsText="No matching parameters"
                    columns={[
                      {
                        field: "jsonParameters",
                        header: "Parameter Set",
                        width: "1fr",
                        renderer: function ParametersCell({
                          rowData,
                          fieldData,
                        }) {
                          const jsonParams = jsonParametersToString(fieldData);
                          return (
                            <div className="flex gap-2">
                              <div>
                                <input
                                  type="checkbox"
                                  id="sample_params"
                                  checked={!!sampleIds.includes(rowData.id)}
                                  onChange={(evt) => {
                                    let newSampleIds: string[];
                                    if (evt.target.checked) {
                                      if (sampleIds.length > 4) {
                                        setErrorMessageForSamples(
                                          "Only up to 5 samples can be selected",
                                        );
                                        return;
                                      }
                                      newSampleIds = [...sampleIds, rowData.id];
                                    } else {
                                      newSampleIds = sampleIds.filter(
                                        (id) => id !== rowData.id,
                                      );
                                    }
                                    setSampleIds(newSampleIds);
                                    if (
                                      hasDuplicatedSamples(
                                        querySamples,
                                        newSampleIds,
                                      )
                                    ) {
                                      setErrorMessageForSamples(
                                        "Some of the selected samples have identical parameters. Duplicated samples will be automatically removed when you run EXPLAIN.",
                                      );
                                    } else {
                                      setErrorMessageForSamples("");
                                    }
                                  }}
                                />
                              </div>
                              <div title={jsonParams}>
                                <SQL
                                  className="!whitespace-nowrap"
                                  sql={jsonParams}
                                />
                                <div className="text-[12px] text-[#606060]">
                                  {formatTimestampShort(
                                    moment.unix(rowData.occurredAt),
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        },
                        disableSort: true,
                      },
                      {
                        field: "explain",
                        header: "Current Plan",
                        width: "140px",
                        renderer: function PlanFingerprintCell({
                          fieldData,
                          rowData,
                        }) {
                          const fingerprint = fieldData.fingerprint;
                          return (
                            <Link
                              to={databaseQueryExplain(
                                databaseId,
                                baselineQuery.query.id,
                                rowData.explain.humanId,
                              )}
                            >
                              <ExplainFingerprint fingerprint={fingerprint} />
                            </Link>
                          );
                        },
                        nullValue: "-",
                      },
                      {
                        field: "runtimeMs",
                        header: "Runtime",
                        width: "140px",
                        renderer: MsCell,
                        defaultSortOrder: "DESC",
                      },
                    ]}
                  />
                </>
              )}
              {errorMessageForSamples && (
                <div className="text-[#C22426]">
                  <FontAwesomeIcon icon={faExclamationCircle} />{" "}
                  {errorMessageForSamples}
                </div>
              )}
            </>
          ) : (
            <CreateParameterSetPanel
              key={`${addParamMethod}-${copyFromId}`}
              workbook={workbook}
              addParamMethod={addParamMethod}
              initialCopyFromId={copyFromId || undefined}
              onParameterSetsCreated={() => setCopyFromId("")}
            />
          )}
        </PanelSection>
      </Panel>
      {parameterSetIdForEdit && (
        <EditParameterSetPanel
          onDismiss={handleEditDismiss}
          workbook={workbook}
          parameterSetId={parameterSetIdForEdit}
        />
      )}
      {showParameterSettingsEditPanel && (
        <EditParameterSettingsPanel
          onDismiss={() => setShowParameterSettingsEditPanel(false)}
          workbook={workbook}
        />
      )}
      <Panel title="Selected Parameter Sets">
        <PanelSection className="grid gap-4">
          {selectedParameters.length === 0 && (
            <div>
              {queryWithNoParams
                ? "This query does not require any parameter sets."
                : "You did not add any parameter sets yet, please choose at least one."}
            </div>
          )}
          {selectedParameters.length === 1 &&
            selectedParameters[0].autoCreatedFromQuery && (
              <Callout thin>
                We created a parameter set from the original query and selected
                it for you.
              </Callout>
            )}
          {selectedParameters.length > 0 && (
            <Grid
              data={selectedParameters}
              columns={[
                {
                  field: "paramInSql",
                  header: "Parameter Set",
                  width: "1fr",
                  renderer: function ParameterSetCell({ fieldData }) {
                    return <SQL sql={fieldData} />;
                  },
                },
                {
                  field: "fingerprint",
                  header: "Current Plan",
                  width: "120px",
                  renderer: function CurrentPlanCell({ fieldData }) {
                    return <ExplainFingerprint fingerprint={fieldData} />;
                  },
                  nullValue: "-",
                },
                {
                  field: "onDelete",
                  header: "",
                  width: "100px",
                  renderer: function OnDeleteCell({ fieldData, rowData }) {
                    return (
                      <div className="text-right">
                        {rowData.onEdit && (
                          <Button bare title="Edit" onClick={rowData.onEdit}>
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="text-link mr-3 cursor-pointer"
                            />
                          </Button>
                        )}
                        {rowData.onClone && (
                          <Button
                            bare
                            title="Create a new parameter set from this one"
                            onClick={rowData.onClone}
                          >
                            <FontAwesomeIcon
                              icon={faClonePlus}
                              className="text-[#606060] mr-3 cursor-pointer"
                            />
                          </Button>
                        )}
                        <Button bare title="Delete" onClick={fieldData}>
                          <FontAwesomeIcon
                            icon={faTrashAlt}
                            className="text-[#CA1515] mr-1 cursor-pointer"
                          />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
            />
          )}
          {errorMessage && (
            <div className="text-[#C22426]">
              <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
            </div>
          )}
          <div>
            <Button
              bare
              className="btn btn-success"
              disabled={!queryWithNoParams && selectedParameters.length === 0}
              onClick={handleRunExplain}
            >
              Run EXPLAIN...
            </Button>
          </div>
        </PanelSection>
      </Panel>
    </PageContent>
  );
};

export const WorkbookCreationHeaderWithWorkbook = ({
  workbook,
}: {
  workbook: WorkbookType;
}) => {
  const [name, setName] = useState(workbook.name);
  const [description, setDescription] = useState(workbook.description || "");
  const { databaseWorkbooks } = useRoutes();
  const navigate = useNavigate();

  const [deleteWorkbook] = useDeleteWorkbookMutation();

  const handleDeleteWorkbook = () => {
    if (!window.confirm("Cancel workbook creation?")) {
      return;
    }
    deleteWorkbook({
      variables: { workbookId: workbook.id },
      refetchQueries: [
        {
          query: WorkbooksDocument,
          variables: {
            databaseId: workbook.databaseId,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        navigate(databaseWorkbooks(workbook.databaseId));
      },
    });
  };

  return (
    <WorkbookCreationHeader
      name={name}
      description={description}
      setName={setName}
      setDescription={setDescription}
      handleDeleteWorkbook={handleDeleteWorkbook}
      workbook={workbook}
    />
  );
};

export const WorkbookCreationHeader = ({
  name,
  description,
  setName,
  setDescription,
  handleDeleteWorkbook,
  workbook,
}: {
  name: string;
  description: string;
  setName: (name: string) => void;
  setDescription: (desc: string) => void;
  handleDeleteWorkbook: () => void;
  workbook?: WorkbookType;
}) => {
  const [showWorkbookNameEditPanel, setShowWorkbookNameEditPanel] =
    useState(false);

  function handleEditShow() {
    setShowWorkbookNameEditPanel(true);
  }
  function handleEditDismiss() {
    setShowWorkbookNameEditPanel(false);
  }

  return (
    <div className="flex flex-row justify-between items-center">
      <div>
        <h2 className="text-[22px] text-[#606060] m-0 py-[9px] font-medium overflow-visible leading-[26px]">
          {name}
        </h2>
        <div className="text-[14px] leading-5 font-normal flex">
          {description && (
            <span className="mr-2 max-w-[320px] truncate">{description}</span>
          )}
          <Button bare onClick={handleEditShow} variant="link">
            {description ? "Edit" : "Edit name and description"}
          </Button>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          bare
          onClick={handleDeleteWorkbook}
          className="text-[14px] text-[#606060] font-normal"
        >
          Cancel workflow
        </Button>
      </div>
      {showWorkbookNameEditPanel && (
        <WorkbookNameEditPanel
          onDismiss={handleEditDismiss}
          name={name}
          description={description}
          setName={setName}
          setDescription={setDescription}
          workbook={workbook}
        />
      )}
    </div>
  );
};

export const CreateSteps = ({
  step,
  workbook,
}: {
  step: "step1" | "step2" | "step3";
  workbook?: WorkbookType;
}) => {
  const navigate = useNavigate();
  const { databaseWorkbooksNew, databaseWorkbookVariant } = useRoutes();

  const active = "text-[#29426D]";
  const inactive = "text-[#757575]";
  const step1Class = step === "step1" ? active : inactive;
  const step2Class = step === "step2" ? active : inactive;
  const step3Class = step === "step3" ? active : inactive;

  const [deleteWorkbook] = useDeleteWorkbookMutation();

  const [deleteWorkbookResults] = useDeleteWorkbookResultsMutation();

  const handleBackToReviewQuery = () => {
    if (
      window.confirm(
        "Going back to the Review query step? All selected parameter sets will be cleared.",
      )
    ) {
      deleteWorkbook({
        variables: { workbookId: workbook.id },
        onCompleted: () => {
          navigate(databaseWorkbooksNew(workbook.databaseId), {
            state: {
              queryText: workbook.baselineQuery.queryText,
              name: workbook.name,
              description: workbook.description,
            },
          });
        },
      });
    }
  };
  const handleBackToChooseParameters = () => {
    if (
      window.confirm(
        "Going back to the Choose parameters step? All uploaded or executed EXPLAIN results will be cleared.",
      )
    ) {
      deleteWorkbookResults({
        variables: {
          variantId: workbook.baselineQuery.id,
          parameterSetsSelected: false,
        },
        refetchQueries: [
          {
            query: WorkbookDetailsDocument,
            variables: {
              workbookId: workbook.id,
              databaseId: workbook.databaseId,
            },
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          navigate(
            databaseWorkbookVariant(
              workbook.databaseId,
              workbook.id,
              workbook.baselineQuery.id,
            ),
          );
        },
      });
    }
  };

  return (
    <div className="flex justify-center align-middle text-[14px] gap-4 font-medium">
      <Button
        bare
        className={classNames(step1Class, step !== "step1" && "cursor-pointer")}
        onClick={step !== "step1" ? handleBackToReviewQuery : undefined}
      >
        <FontAwesomeIcon icon={faCircle1} /> Review query
      </Button>
      <Button
        bare
        className={classNames(step2Class, step === "step3" && "cursor-pointer")}
        onClick={step === "step3" ? handleBackToChooseParameters : undefined}
      >
        <FontAwesomeIcon icon={faCircle2} /> Choose parameters
      </Button>
      <div className={step3Class}>
        <FontAwesomeIcon icon={faCircle3} /> Run EXPLAIN
      </div>
    </div>
  );
};

export default ChooseParameters;
